<template>
  <div class="purchase-plan-list" role="radiogroup" :aria-label="t('pro_dialog_choose_menu_title')">
    <button
      v-for="(item, index) in proList"
      :key="item.choiceId || `custom-${index}`"
      type="button"
      class="purchase-plan-option"
      :class="{
        'is-selected': !item.isCustomized && gearRadioValue === index,
        'is-disabled': isPlanDisabled(item),
        'is-custom': item.isCustomized,
      }"
      :disabled="isPlanDisabled(item)"
      :title="getDisabledMessage(item)"
      :role="item.isCustomized ? undefined : 'radio'"
      :aria-checked="item.isCustomized ? undefined : gearRadioValue === index"
      @click="selectPlan(item, index)"
    >
      <EnterpriseIcon
        :src="gearRadioValue === index && !item.isCustomized ? circleDotIcon : circleIcon"
        class="purchase-plan-option__radio"
      />
      <span class="purchase-plan-option__body">
        <span class="purchase-plan-option__name-row">
          <strong>{{ getPlanTitle(item) }}</strong>
          <span v-if="isCurrentPlan(item)" class="purchase-plan-option__current">{{ t('enterprise_ui_purchase_current_plan') }}</span>
          <small>{{ item.isCustomized ? t('enterprise_home_comparison_custom') : formatQuota(item.charNum, item.choiceId) }}</small>
        </span>
        <b>{{ item.isCustomized ? t('enterprise_ui_purchase_contact_sales') : formatPrice(item) }}</b>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import circleDotIcon from '~/assets/images/enterprise/icons/circle-dot.svg'
import circleIcon from '~/assets/images/enterprise/icons/circle.svg'
import { getProBusinessSubscribeConfig } from '~/config/proConfig'
import type { EnterprisePricingMode } from '~/config/enterpriseLanding'
import type { SubscribeConfigItem } from '~/types/pro'
import useProStore from '~/store/modules/pro'
import useUserStore from '~/store/modules/user'
import {
  getEnterprisePlanPurchaseDecision,
} from '~/utils/enterpriseTrade'
import type { EnterprisePlanBlockReason } from '~/utils/enterpriseTrade'

interface Props {
  coin?: string
  pricingMode?: EnterprisePricingMode
}

const props = withDefaults(defineProps<Props>(), {
  pricingMode: 'local',
})
const emit = defineEmits<{
  contact: []
  'selection-change': [item?: SubscribeConfigItem]
}>()
const userStore = useUserStore()
const proStore = useProStore()
const { t, locale } = useI18n()
const numberLocale = computed(() => ({ zh: 'zh-CN', tw: 'zh-TW', 'zh-tw': 'zh-TW', itb: 'it' } as Record<string, string>)[locale.value.toLowerCase()] || locale.value)
const proList = ref<SubscribeConfigItem[]>([])
const gearRadioValue = ref(-1)
const pendingChoiceId = ref('')

function isCurrentPlan(item: SubscribeConfigItem) {
  return getEnterprisePlanPurchaseDecision(userStore.bizTeamInfo, item.choiceId).reason === 'current-plan'
}

function isPlanDisabled(item: SubscribeConfigItem) {
  return !item.isCustomized && !getEnterprisePlanPurchaseDecision(userStore.bizTeamInfo, item.choiceId).allowed
}

function getDisabledMessage(item: SubscribeConfigItem) {
  if (item.isCustomized) return undefined
  const decision = getEnterprisePlanPurchaseDecision(userStore.bizTeamInfo, item.choiceId)
  if (decision.allowed) return undefined
  const keys: Record<EnterprisePlanBlockReason, string> = {
    'unknown-plan': 'enterprise_ui_purchase_select_plan',
    'unknown-entitlement': 'enterprise_ui_purchase_unknown_entitlement',
    'global-to-local': 'enterprise_ui_purchase_global_to_local',
    downgrade: 'enterprise_ui_purchase_downgrade',
    'current-plan': 'enterprise_ui_purchase_no_renewal',
  }
  return t(keys[decision.reason])
}

function selectPlan(item: SubscribeConfigItem, index: number) {
  if (isPlanDisabled(item)) return
  if (item.isCustomized) {
    emit('contact')
    return
  }
  gearRadioValue.value = index
  emit('selection-change', item)
}

function firstAvailableIndex() {
  return proList.value.findIndex(item => !item.isCustomized && !isPlanDisabled(item))
}

function resetSelection() {
  const targetIndex = pendingChoiceId.value
    ? proList.value.findIndex(item => item.choiceId === pendingChoiceId.value)
    : -1
  const selectableTarget = targetIndex >= 0 && !isPlanDisabled(proList.value[targetIndex])
    ? targetIndex
    : firstAvailableIndex()
  gearRadioValue.value = selectableTarget
  emit('selection-change', selectableTarget >= 0 ? proList.value[selectableTarget] : undefined)
}

function getProList() {
  const coin = props.coin?.toLowerCase()
  proList.value = getProBusinessSubscribeConfig(coin, props.pricingMode)
  resetSelection()
}

function refreshTranslatedPlans() {
  const selectedChoiceId = getPayConfigItem()?.choiceId
  proList.value = getProBusinessSubscribeConfig(props.coin?.toLowerCase(), props.pricingMode)
  const selectedIndex = proList.value.findIndex(item => item.choiceId === selectedChoiceId && !item.isCustomized)
  if (selectedIndex < 0 || isPlanDisabled(proList.value[selectedIndex])) {
    resetSelection()
    return
  }
  gearRadioValue.value = selectedIndex
  emit('selection-change', proList.value[selectedIndex])
}

function setDefaultItem(item: SubscribeConfigItem) {
  pendingChoiceId.value = item?.choiceId || ''
  resetSelection()
}

function getPayConfigItem() {
  const item = gearRadioValue.value >= 0 ? proList.value[gearRadioValue.value] : undefined
  return item && !isPlanDisabled(item) ? item : undefined
}

function getPlanTitle(item: SubscribeConfigItem) {
  if (item.isCustomized || props.pricingMode !== 'global') return item.title
  return t('enterprise_ui_pricing_global_plan', { name: item.title })
}

function formatQuota(value?: number | string, choiceId?: string) {
  const amount = Number(value || 0)
  if (!Number.isFinite(amount) || amount <= 0) return t('enterprise_ui_pricing_plan_quota_unknown')
  const count = new Intl.NumberFormat(numberLocale.value, { notation: 'compact', maximumFractionDigits: 2 }).format(amount)
  const quota = t('enterprise_ui_pricing_characters_year', { count })
  const reason = getEnterprisePlanPurchaseDecision(userStore.bizTeamInfo, choiceId).reason
  if (reason === 'downgrade') return t('enterprise_ui_pricing_quota_lower', { quota })
  return props.pricingMode === 'global' && reason !== 'current-plan' ? t('enterprise_ui_pricing_quota_dual', { quota }) : quota
}

function formatPrice(item: SubscribeConfigItem) {
  const value = userStore.bizTeamInfo?.agentUid ? item.bizAgentRealPrice : item.realPrice
  const symbol = userStore.getCurrentCoinSymbol(props.coin || '')
  return t('enterprise_ui_pricing_price_year', { price: `${symbol}${formatNumber(Number(value || 0))}` })
}

function formatNumber(value: number) {
  return new Intl.NumberFormat(numberLocale.value, { maximumFractionDigits: 2 }).format(value)
}

watch(
  () => [
    props.coin,
    props.pricingMode,
    proStore.proConfig,
    proStore.globalProConfig,
    userStore.roleRightsMap,
    userStore.bizTeamInfo?.serviceLevel,
    userStore.bizTeamInfo?.serviceEndTime,
    userStore.bizTeamInfo?.globalContractNo,
    userStore.bizTeamInfo?.globalPlanCode,
    userStore.bizTeamInfo?.regionMode,
    userStore.bizTeamInfo?.legacyMigrationNo,
  ],
  getProList,
  { immediate: true, deep: true },
)
watch(locale, refreshTranslatedPlans)

defineExpose({ setDefaultItem, getPayConfigItem, resetSelection })
</script>

<style lang="scss" scoped>
.purchase-plan-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.purchase-plan-option {
  width: 100%;
  min-height: 45px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  color: #1f2329;
  text-align: left;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;

  &:hover:not(:disabled),
  &.is-selected {
    border-color: #2b7de9;
    background: #f5f9ff;
  }

  &.is-selected {
    box-shadow:
      inset 0 0 0 0.5px #2b7de9,
      0 0 0 1px rgba(43, 125, 233, 0.04);
  }

  &.is-disabled {
    color: #c3c9d1;
    border-color: #e3e7ec;
    background: #fff;
    cursor: not-allowed;
  }

  &.is-custom:not(.is-disabled) {
    cursor: pointer;
  }

  &__radio {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    color: #c3c9d1;
  }

  &.is-selected &__radio {
    color: #2b7de9;
  }

  &.is-selected &__name-row strong {
    color: #2b7de9;
  }

  &__body {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    > b {
      flex: 0 0 auto;
      color: #2b7de9;
      font-size: 14.5px;
      font-weight: 800;
      line-height: 21px;
      white-space: nowrap;
    }
  }

  &__name-row {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    strong {
      flex: 0 0 auto;
      font-size: 13.5px;
      font-weight: 700;
      line-height: 20px;
    }

    small {
      overflow: hidden;
      color: #8b95a1;
      font-size: 11.5px;
      line-height: 17px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__current {
    flex: 0 0 auto;
    padding: 2px 8px;
    color: #98a2b3;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    border-radius: 8px;
    background: #f2f4f7;
  }

  &.is-disabled &__name-row small,
  &.is-disabled &__body > b {
    color: #c3c9d1;
  }

  &.is-disabled &__radio { color: #e3e7ec; }
}

@media (max-width: 640px) {
  .purchase-plan-option {
    &__body {
      align-items: flex-start;
    }

    &__name-row {
      flex-wrap: wrap;
      gap: 3px 8px;
    }
  }
}
</style>
